'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('Global Error caught:', error);
  }, [error]);

  return (
    <html lang="en">
      <body className="bg-background font-sans text-on-surface antialiased">
        <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4">
          <div className="pointer-events-none absolute inset-0 h-full w-full overflow-hidden">
            <div
              className="bg-red-500 absolute top-[-10%] right-[-10%] h-[60%] w-[60%] animate-pulse rounded-full opacity-[0.03] mix-blend-screen blur-[120px] dark:opacity-[0.05]"
              style={{ animationDuration: '8s' }}
            />
            <div
              className="bg-primary absolute bottom-[-10%] left-[-10%] h-[60%] w-[60%] animate-pulse rounded-full opacity-[0.03] mix-blend-screen blur-[120px] dark:opacity-[0.05]"
              style={{ animationDuration: '10s' }}
            />
          </div>

          <div className="relative z-10 flex max-w-md flex-col items-center text-center">
            <div className="bg-error-container text-error shadow-error/20 mb-8 flex h-20 w-20 items-center justify-center rounded-3xl shadow-lg">
              <AlertCircle size={40} />
            </div>

            <h1 className="text-on-background text-4xl font-black tracking-tight">
              Something went wrong
            </h1>

            <p className="text-on-surface-variant mt-4 leading-relaxed font-medium">
              We've encountered an unexpected error. Please try refreshing the page.
            </p>

            <div className="mt-10 flex w-full flex-col gap-4 sm:w-auto sm:flex-row">
              <button
                onClick={() => reset()}
                className="bg-primary text-on-primary hover:bg-primary/90 shadow-primary/20 flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-bold shadow-lg transition-all hover:-translate-y-0.5 active:translate-y-0"
              >
                <RefreshCw size={18} />
                Try Again
              </button>

              <Link
                href="/"
                className="border-outline-variant bg-surface text-on-surface hover:bg-surface-container-high flex items-center justify-center gap-2 rounded-xl border px-6 py-3 text-sm font-bold transition-colors"
              >
                <Home size={18} />
                Go to Home
              </Link>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
